import { format } from 'date-fns';
import { CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

const tooltipStyle = {
	background: 'hsl(var(--popover))',
	border: '1px solid hsl(var(--border))',
	borderRadius: 8,
	fontSize: 12
};

const dayLabel = (value) => {
	if (!value) return '';
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return value;
	return format(d, 'd MMM');
};

/**
 * Pads the previous-period series so each point aligns with the matching
 * day in `current`. Without this, two windows of different length would
 * render with the prev line shorter or shifted on the x-axis.
 */
function alignSeries(current, previous) {
	const offset = previous.length - current.length;
	return current.map((row, i) => ({
		date: dayLabel(row.date),
		total: row.total,
		prev: previous[i + offset]?.total ?? null
	}));
}

export function ProfileViewsChart({ series, previousSeries }) {
	const data = alignSeries(series, previousSeries);
	return (
		<div className="mt-2 h-64 w-full">
			<ResponsiveContainer width="100%" height="100%">
				<LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
					<CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
					<XAxis
						dataKey="date"
						tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
						tickLine={false}
						axisLine={false}
						interval={Math.max(0, Math.floor(data.length / 6))}
					/>
					<YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} width={32} />
					<Tooltip contentStyle={tooltipStyle} />
					<Line
						type="monotone"
						dataKey="prev"
						name="Previous period"
						stroke="hsl(var(--muted-foreground))"
						strokeWidth={1.5}
						strokeDasharray="4 4"
						dot={false}
					/>
					<Line type="monotone" dataKey="total" name="Profile views" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={false} />
				</LineChart>
			</ResponsiveContainer>
		</div>
	);
}

export function MapsSearchChart({ series }) {
	const data = series.map((r) => ({ date: dayLabel(r.date), search: r.search, maps: r.maps }));
	return (
		<div className="mt-2 h-64 w-full">
			<ResponsiveContainer width="100%" height="100%">
				<LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
					<CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
					<XAxis
						dataKey="date"
						tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
						tickLine={false}
						axisLine={false}
						interval={Math.max(0, Math.floor(data.length / 6))}
					/>
					<YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} width={32} />
					<Tooltip contentStyle={tooltipStyle} />
					<Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} iconType="circle" />
					<Line type="monotone" dataKey="search" name="Search views" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={false} />
					<Line type="monotone" dataKey="maps" name="Maps views" stroke="hsl(var(--action))" strokeWidth={2.5} dot={false} />
				</LineChart>
			</ResponsiveContainer>
		</div>
	);
}

export function InteractionsBreakdownChart({ website, directions, phone }) {
	const data = [
		{ name: 'Website clicks', value: website, color: 'hsl(var(--primary))' },
		{ name: 'Directions clicks', value: directions, color: 'hsl(var(--action))' },
		{ name: 'Phone clicks', value: phone, color: 'hsl(var(--success))' }
	];
	const total = data.reduce((t, x) => t + x.value, 0);
	if (total === 0) {
		return <p className="mt-4 text-sm text-muted-foreground">No profile interactions in this period yet.</p>;
	}
	return (
		<div className="mt-2 flex items-center gap-6">
			<div className="relative h-44 w-44 shrink-0">
				<ResponsiveContainer width="100%" height="100%">
					<PieChart>
						<Pie
							data={data}
							dataKey="value"
							nameKey="name"
							innerRadius={48}
							outerRadius={78}
							paddingAngle={2}
							stroke="white"
							strokeWidth={2}
						>
							{data.map((seg) => (
								<Cell key={seg.name} fill={seg.color} />
							))}
						</Pie>
						<Tooltip contentStyle={tooltipStyle} />
					</PieChart>
				</ResponsiveContainer>
				<div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
					<div className="text-2xl font-extrabold tabular-nums">{total.toLocaleString('en-GB')}</div>
					<div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Total</div>
				</div>
			</div>
			<ul className="min-w-0 flex-1 space-y-3">
				{data.map((seg) => {
					const pct = Math.round((seg.value / total) * 100);
					return (
						<li key={seg.name} className="flex items-center gap-3">
							<span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: seg.color }} />
							<span className="flex-1 truncate text-sm font-medium text-foreground">{seg.name}</span>
							<span className="text-sm font-semibold tabular-nums">{seg.value.toLocaleString('en-GB')}</span>
							<span className="w-9 text-right text-xs tabular-nums text-muted-foreground">{pct}%</span>
						</li>
					);
				})}
			</ul>
		</div>
	);
}

export function InteractionsTimeChart({ series }) {
	const data = series.map((r) => ({
		date: dayLabel(r.date),
		website: r.website,
		directions: r.directions,
		phone: r.phone
	}));
	return (
		<div className="mt-2 h-64 w-full">
			<ResponsiveContainer width="100%" height="100%">
				<LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
					<CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
					<XAxis
						dataKey="date"
						tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
						tickLine={false}
						axisLine={false}
						interval={Math.max(0, Math.floor(data.length / 6))}
					/>
					<YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} width={32} />
					<Tooltip contentStyle={tooltipStyle} />
					<Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} iconType="circle" />
					<Line type="monotone" dataKey="website" name="Website" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={false} />
					<Line
						type="monotone"
						dataKey="directions"
						name="Directions"
						stroke="hsl(var(--action))"
						strokeWidth={2.5}
						dot={false}
					/>
					<Line type="monotone" dataKey="phone" name="Phone" stroke="hsl(var(--success))" strokeWidth={2.5} dot={false} />
				</LineChart>
			</ResponsiveContainer>
		</div>
	);
}
